/**
 * Adding a task, and changing one.
 *
 * One form for both, in a drawer that comes up from the bottom on a phone —
 * where the thumb is — and from the side on anything wider.
 *
 * Saving an existing task sends only what changed, field by field, because
 * the server passes exactly those fields on to ClickUp. Sending the whole
 * form back would overwrite whatever somebody typed in ClickUp in the
 * meantime with what this screen loaded a minute ago.
 */

import { Trash2 } from 'lucide-react'
import { useState } from 'react'
import { api, ApiError } from '../../api'
import { Button } from '../../components/Button'
import { ConfirmDialog } from '../../components/ConfirmDialog'
import { Drawer } from '../../components/Drawer'
import { SegmentedControl } from '../../components/FilterBar'
import { useAppData } from '../../context/AppDataContext'
import { useAuth } from '../../context/AuthContext'
import { useToast } from '../../context/ToastContext'
import { cn } from '../../lib/cn'
import { DateField, Field, SelectField, TextField } from '../../ui/Field'
import { SourceChip } from '../Tasks'
import { useWorkspace } from '../../context/WorkspaceContext'
import { useApi } from '../../useApi'
import type { Project } from '../projects/types'
import type { StudioTask } from './types'

interface Draft {
  title: string
  description: string
  client: string
  due: string
  priority: string
  status: StudioTask['status']
  assignees: string[]
  tags: string
  /** The project it is filed under, and the phase inside it. */
  project: string
  phase: string
}

function draftOf(t: StudioTask | 'new', me: string | null): Draft {
  if (t === 'new') {
    return {
      title: '',
      description: '',
      client: '',
      due: '',
      priority: '',
      status: 'todo',
      assignees: me === null ? [] : [me],
      tags: '',
      project: '',
      phase: ''
    }
  }
  return {
    title: t.title,
    description: t.description ?? '',
    client: t.client_id ?? '',
    due: t.due ?? '',
    priority: t.priority ?? '',
    status: t.status,
    assignees: [...t.assignee_ids],
    tags: t.tags.join(', '),
    project: t.project_id ?? '',
    phase: t.phase_id ?? ''
  }
}

function tagsOf(s: string): string[] {
  return [...new Set(s.split(',').map((t) => t.trim().toLowerCase()).filter((t) => t !== ''))]
}

const same = (a: readonly string[], b: readonly string[]) =>
  a.length === b.length && [...a].sort().every((x, i) => x === [...b].sort()[i])

export function TaskDrawer({
  task,
  onClose,
  onSaved
}: {
  task: StudioTask | 'new' | null
  onClose: () => void
  onSaved: () => void
}) {
  const { clients, currentClients, directory } = useAppData()
  const { currentUser, hasRole } = useAuth()
  const { showToast } = useToast()
  const { taskCategories: categories } = useWorkspace()
  const me = currentUser?.linkedPersonaId ?? null
  const canEdit = hasRole('PARTNER', 'MANAGER', 'TEAM')

  const [d, setD] = useState<Draft>(() => draftOf('new', me))
  // Current clients only, plus the one this task already has if that client
  // has since left, so opening an old task does not quietly lose its client.
  const kept = currentClients.some((c) => c.id === d.client) ? undefined : clients.find((c) => c.id === d.client)
  const clientOptions = kept === undefined ? currentClients : [...currentClients, kept]
  const [busy, setBusy] = useState(false)
  const [confirming, setConfirming] = useState(false)

  // The live projects, so a task can be filed under one without leaving the
  // form. One small read, made whether or not the drawer is open: the picker
  // has to be populated the instant it is, and the alternative is a spinner
  // inside a form somebody is already typing into.
  const { result: projectResult } = useApi<Project[]>('/projects')
  const projects = projectResult.data ?? []
  const filedUnder = projects.find((p) => p.id === d.project)

  // Reset while rendering rather than in an effect, so the first frame of a
  // freshly opened task shows that task and not the blank form it replaced.
  const [shown, setShown] = useState<StudioTask | 'new' | null>(null)
  if (task !== shown) {
    setShown(task)
    if (task !== null) setD(draftOf(task, me))
  }

  const set = <K extends keyof Draft>(k: K, v: Draft[K]) => setD((x) => ({ ...x, [k]: v }))

  async function save() {
    if (task === null || d.title.trim() === '') return
    setBusy(true)
    try {
      if (task === 'new') {
        await api('/tasks', {
          method: 'POST',
          body: {
            title: d.title.trim(),
            ...(d.description.trim() === '' ? {} : { description: d.description.trim() }),
            ...(d.client === '' ? {} : { client: d.client }),
            ...(d.due === '' ? {} : { due: d.due }),
            ...(d.priority === '' ? {} : { priority: d.priority }),
            status: d.status,
            assigneeIds: d.assignees,
            tags: tagsOf(d.tags),
            ...(d.project === '' ? {} : { projectId: d.project }),
            ...(d.project === '' || d.phase === '' ? {} : { phaseId: d.phase })
          }
        })
        showToast('Task added. It will be in ClickUp in a few seconds.')
      } else {
        const was = draftOf(task, me)
        const patch: Record<string, unknown> = {}
        if (d.title.trim() !== was.title) patch['title'] = d.title.trim()
        if (d.description.trim() !== was.description) patch['description'] = d.description.trim() === '' ? null : d.description.trim()
        if (d.client !== was.client) patch['client'] = d.client === '' ? null : d.client
        if (d.due !== was.due) patch['due'] = d.due === '' ? null : d.due
        if (d.priority !== was.priority) patch['priority'] = d.priority === '' ? null : d.priority
        if (!same(tagsOf(d.tags), task.tags)) patch['tags'] = tagsOf(d.tags)

        if (Object.keys(patch).length > 0) await api(`/tasks/${task.id}`, { method: 'PATCH', body: patch })
        if (d.status !== was.status) await api(`/tasks/${task.id}/status`, { method: 'PATCH', body: { status: d.status } })
        if (!same(d.assignees, was.assignees)) {
          await api(`/tasks/${task.id}/assignees`, { method: 'PATCH', body: { assigneeIds: d.assignees } })
        }
        // Where a task is filed is its own call: ClickUp is not told, because
        // a task is the same task wherever in Hapi it sits.
        if (d.project !== was.project || d.phase !== was.phase) {
          await api(`/tasks/${task.id}/project`, {
            method: 'PATCH',
            body: {
              projectId: d.project === '' ? null : d.project,
              phaseId: d.project === '' || d.phase === '' ? null : d.phase
            }
          })
        }
        showToast('Saved.')
      }
      onSaved()
    } catch (err) {
      showToast(err instanceof ApiError ? err.message : 'That did not save.', 'error')
    } finally {
      setBusy(false)
    }
  }

  async function remove() {
    if (task === null || task === 'new') return
    setConfirming(false)
    setBusy(true)
    try {
      await api(`/tasks/${task.id}`, { method: 'DELETE' })
      showToast('Deleted, here and in ClickUp.')
      onSaved()
    } catch (err) {
      showToast(err instanceof ApiError ? err.message : 'That did not delete.', 'error')
    } finally {
      setBusy(false)
    }
  }

  const isNew = task === 'new'
  const wide = typeof window !== 'undefined' && window.matchMedia('(min-width: 640px)').matches

  return (
    <Drawer
      open={task !== null}
      onClose={onClose}
      title={isNew ? 'Add a task' : 'Task'}
      side={wide ? 'right' : 'bottom'}
      widthClassName="max-w-md"
    >
      <form
        className="flex flex-col gap-4 px-5 py-5 sm:px-6"
        onSubmit={(e) => {
          e.preventDefault()
          void save()
        }}
      >
        {!isNew && task !== null && (task.source !== 'hapi' || task.clickup_id !== null) && (
          <div className="flex flex-wrap items-center gap-2 text-xs text-ink-500">
            <SourceChip source={task.source} />
            {task.clickup_id === null ? (
              <span>Not in ClickUp yet</span>
            ) : (
              <a
                href={`https://app.clickup.com/t/${task.clickup_id}`}
                target="_blank"
                rel="noreferrer"
                className="font-medium text-shell-text underline decoration-ink-300 underline-offset-2 hover:decoration-ink-600"
              >
                Open in ClickUp
              </a>
            )}
          </div>
        )}

        <Field
          label="What needs doing"
          value={d.title}
          onChange={(e) => set('title', e.target.value)}
          required
          maxLength={2000}
          autoFocus={isNew}
          disabled={!canEdit}
        />

        <div>
          <span className="text-sm font-medium text-shell-text">Where it is</span>
          <div className="mt-1.5">
            <SegmentedControl
              value={d.status}
              onChange={(v) => canEdit && set('status', v as StudioTask['status'])}
              options={[
                { label: 'To do', value: 'todo' },
                { label: 'In progress', value: 'in_progress' },
                { label: 'Done', value: 'done' }
              ]}
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <DateField label="Due" value={d.due} onChange={(e) => set('due', e.target.value)} disabled={!canEdit} placeholder="No due date" />
          <SelectField label="Priority" value={d.priority} onChange={(e) => set('priority', e.target.value)} disabled={!canEdit}>
            <option value="">None</option>
            <option value="low">Low</option>
            <option value="medium">Medium</option>
            <option value="high">High</option>
            <option value="urgent">Urgent</option>
          </SelectField>
        </div>

        <SelectField label="Client" value={d.client} onChange={(e) => set('client', e.target.value)} disabled={!canEdit}>
          <option value="">Not for a client</option>
          {clientOptions.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </SelectField>

        {(projects.length > 0 || d.project !== '') && (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <SelectField
              label="Part of"
              value={d.project}
              onChange={(e) => setD((x) => ({ ...x, project: e.target.value, phase: '' }))}
              disabled={!canEdit}
            >
              <option value="">Not part of a project</option>
              {projects.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </SelectField>
            {filedUnder !== undefined && filedUnder.phases.length > 0 && (
              <SelectField label="Phase" value={d.phase} onChange={(e) => set('phase', e.target.value)} disabled={!canEdit}>
                <option value="">Not said yet</option>
                {filedUnder.phases.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </SelectField>
            )}
          </div>
        )}

        <fieldset>
          <legend className="text-sm font-medium text-shell-text">With</legend>
          <div className="mt-1.5 flex flex-wrap gap-1.5">
            {directory.map((p) => {
              const on = d.assignees.includes(p.id)
              return (
                <button
                  key={p.id}
                  type="button"
                  aria-pressed={on}
                  disabled={!canEdit}
                  onClick={() => set('assignees', on ? d.assignees.filter((x) => x !== p.id) : [...d.assignees, p.id])}
                  className={cn(
                    'flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-sm transition-colors',
                    on ? 'border-shell-text-active bg-shell-text-active text-shell' : 'border-shell-border bg-shell text-shell-text hover:bg-shell-border/40'
                  )}
                >
                  <span className="h-2 w-2 rounded-full" style={{ backgroundColor: p.color }} />
                  {p.name.split(' ')[0]}
                </button>
              )
            })}
          </div>
          {d.assignees.length === 0 && (
            <p className="mt-1 text-xs text-ink-500">With nobody on it, it goes into your own ClickUp list.</p>
          )}
        </fieldset>

        <TextField
          label="Notes"
          rows={4}
          value={d.description}
          onChange={(e) => set('description', e.target.value)}
          disabled={!canEdit}
        />

        <div className="flex flex-col gap-2">
          <Field
            label="Tags"
            hint="Separated by commas."
            value={d.tags}
            onChange={(e) => set('tags', e.target.value)}
            disabled={!canEdit}
          />
          {canEdit && categories.some((c) => !tagsOf(d.tags).includes(c.toLowerCase())) && (
            <div className="flex flex-wrap gap-1.5">
              {categories
                .filter((c) => !tagsOf(d.tags).includes(c.toLowerCase()))
                .map((c) => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => set('tags', [...tagsOf(d.tags), c].join(', '))}
                    className="rounded-full border border-dashed border-shell-border px-2.5 py-0.5 text-xs text-shell-text hover:border-brand-400 hover:text-brand-600"
                  >
                    + {c}
                  </button>
                ))}
            </div>
          )}
        </div>

        {canEdit && (
          <div className="flex items-center justify-between gap-3 pt-2">
            {!isNew ? (
              <Button type="button" variant="ghost" onClick={() => setConfirming(true)} disabled={busy}>
                <Trash2 size={15} /> Delete
              </Button>
            ) : (
              <span />
            )}
            <div className="flex gap-2">
              <Button type="button" variant="secondary" onClick={onClose} disabled={busy}>
                Cancel
              </Button>
              <Button type="submit" disabled={busy || d.title.trim() === ''}>
                {isNew ? 'Add task' : 'Save'}
              </Button>
            </div>
          </div>
        )}
      </form>

      <ConfirmDialog
        open={confirming}
        onClose={() => setConfirming(false)}
        onConfirm={() => void remove()}
        title="Delete this task?"
        description="It is deleted in ClickUp too."
        confirmLabel="Delete"
        danger
      />
    </Drawer>
  )
}
